import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, ChevronDown, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.__WR_API_BASE__}/api`;

/**
 * TeammateDraftChip — Compact dropdown chip surfacing today's pickable
 * teammate drafts for the provider's active team. Sits above the notes
 * input as a "baton ready" affordance.
 *
 * Hidden when there's no active team OR no pickable drafts.
 */
const TeammateDraftChip = ({ onPickup }) => {
  const [drafts, setDrafts] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const teamId = (() => {
    try { return localStorage.getItem('writeright_active_team') || null; }
    catch { return null; }
  })();

  const fetchDrafts = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/teams/${teamId}/drafts`, { withCredentials: true });
      const raw = (res.data?.drafts || []).filter(d => !d.claimed_by);
      // Dedupe by patient identity — multiple draft/auto-save versions of the
      // SAME patient should collapse into ONE baton (keep the newest). User
      // feedback Feb 15, 2026: "26 batons ready" was misleading because most
      // were duplicate versions of the same encounter clogging up the list.
      //
      // Identity key prefers patient_label (richest), falls back to
      // {age|gender|title} which covers the older draft shape. Drafts
      // missing both keys stay independent (rare).
      const buckets = new Map();
      for (const d of raw) {
        const key = (d.patient_label || `${d.age || '?'}|${d.gender || '?'}|${(d.title || '').slice(0, 60)}`).toLowerCase().trim();
        const ts = new Date(d.updated_at || d.shared_at || d.created_at || 0).getTime();
        const existing = buckets.get(key);
        if (!existing) {
          buckets.set(key, { d, ts, dupe_count: 1 });
        } else {
          existing.dupe_count += 1;
          if (ts > existing.ts) {
            // Newer version wins; preserve cumulative dupe count
            buckets.set(key, { d, ts, dupe_count: existing.dupe_count });
          }
        }
      }
      const list = Array.from(buckets.values())
        .sort((a, b) => b.ts - a.ts)
        .map(b => ({ ...b.d, _dupe_count: b.dupe_count }));
      setDrafts(list);
    } catch (e) { /* fail silently */ }
    finally { setLoading(false); }
  }, [teamId]);

  // Initial load + refresh every 30s while mounted
  useEffect(() => {
    if (!teamId) return;
    fetchDrafts();
    const interval = setInterval(fetchDrafts, 30000);
    return () => clearInterval(interval);
  }, [teamId, fetchDrafts]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest('[data-testid="teammate-draft-chip"]')) setOpen(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [open]);

  if (!teamId) return null;
  if (drafts.length === 0) return null;

  const handlePick = async (draft) => {
    if (!onPickup) return;
    setOpen(false);
    try {
      await axios.post(`${API}/teams/${teamId}/claim-draft`, { draft_id: draft.draft_id || draft.id }, { withCredentials: true });
      onPickup(draft);
      toast.success(`Baton received from ${draft.shared_by_name || 'teammate'}`);
      fetchDrafts();
    } catch (e) {
      toast.error('Failed to claim draft');
    }
  };

  return (
    <div className="relative inline-block" data-testid="teammate-draft-chip">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 transition-colors"
        data-testid="teammate-draft-chip-btn"
        title="Teammate drafts ready for pickup today"
      >
        <Users className="w-3 h-3" />
        <span>{drafts.length} baton{drafts.length === 1 ? '' : 's'} ready</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-1 z-30 w-80 max-h-72 overflow-y-auto bg-popover border border-border rounded-lg shadow-xl"
          data-testid="teammate-draft-chip-menu"
        >
          <div className="px-3 py-2 border-b border-border bg-secondary/30 sticky top-0">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
              Today's open batons
            </p>
            <p className="text-[10px] text-muted-foreground/70 mt-0.5">Tap to pick up — input loads, output clears.</p>
          </div>
          {loading ? (
            <div className="px-3 py-4 text-xs text-muted-foreground text-center">Loading…</div>
          ) : (
            <ul className="py-1">
              {drafts.map((d) => (
                <li key={d.draft_id || d.id}>
                  <button
                    type="button"
                    onClick={() => handlePick(d)}
                    className="w-full text-left px-3 py-2 hover:bg-secondary/60 transition-colors flex items-start gap-2"
                    data-testid={`teammate-draft-pick-${d.draft_id || d.id}`}
                  >
                    <Clock className="w-3 h-3 text-amber-400/70 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-xs font-semibold text-foreground line-clamp-1 flex-1 min-w-0">
                          {d.title || d.patient_label || 'Untitled draft'}
                        </p>
                        {d._dupe_count > 1 && (
                          <span
                            className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 flex-shrink-0"
                            title={`${d._dupe_count} draft versions of this patient — newest shown`}
                          >
                            +{d._dupe_count - 1} ver
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground line-clamp-1">
                        from {d.shared_by_name || 'teammate'}
                        {d.shared_at && ` · ${new Date(d.shared_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default TeammateDraftChip;
